import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Footer from '../../components/Footer/Footer';
import InstagramSection from '../../components/BrandHomePage/InstagramSection';
import UnifiedNavigation from '../../components/Navigation/UnifiedNavigation';
import { images } from '../../components/shared/assets';
import { initializeAllFeatures } from '../../utils/interactiveFeatures';
import { usePageContent, useGalleryImages } from '../../hooks/useContentful';
import { ContentfulText } from '../../components/Contentful';
import { ContentfulImage } from '../../components/Contentful';
import { FilterBar, DESTINATION_OPTIONS, TYPE_OPTIONS } from '../../components/shared/FilterBar';
import './GalleryAtacama.css';

const GalleryAtacama: React.FC = () => {
  const { t } = useTranslation();
  const [selectedDestination, setSelectedDestination] = useState<string>('atacama');
  const [selectedType, setSelectedType] = useState<string | undefined>(undefined);
  
  // Fetch content from Contentful
  const { data: pageContentData } = usePageContent('gallery');
  const { data: galleryImagesData } = useGalleryImages();
  const pageContent = pageContentData?.[0]?.fields as any;
  const galleryImages = galleryImagesData || [];
  
  // Filter images for Atacama only
  const atacamaImages = galleryImages.filter((image: any) => {
    const destination = image.fields?.destination?.toLowerCase();
    return destination === 'atacama' || !destination;
  });
  
  // Create a helper function to get dynamic alt text
  const getAltText = (fallback: string, imageIndex?: number): string => {
    if (atacamaImages.length > 0 && imageIndex !== undefined && atacamaImages[imageIndex]) {
      const fields = atacamaImages[imageIndex].fields as any;
      return String(fields.altText || fallback);
    }
    return fallback;
  };
  
  // Create a helper function to get image URL
  const getImageUrl = (fallbackUrl: string, imageIndex?: number): string => {
    if (atacamaImages.length > 0 && imageIndex !== undefined && atacamaImages[imageIndex]) {
      const fields = atacamaImages[imageIndex].fields as any;
      const imageField = fields.image as any;
      const imageUrl = imageField?.fields?.file?.url;
      return imageUrl ? `https:${imageUrl}` : fallbackUrl;
    }
    return fallbackUrl;
  };

  // Initialize all interactive features
  useEffect(() => {
    const cleanup = initializeAllFeatures();
    return cleanup;
  }, []);

  // Fallback Atacama gallery images
  const fallbackImages = [
    { src: '/assets/images/gallery-atacama-desert-view.png', alt: 'Atacama Desert View' },
    { src: '/assets/images/destinationsAtacama.png', alt: 'Awasi Atacama Pool' },
    { src: '/assets/images/packageCollage1.png', alt: 'Desert Landscape' },
    { src: '/assets/images/packageCollage2.png', alt: 'Salt Flats' },
    { src: '/assets/images/packageCollage3.png', alt: 'Mountain Vista' },
    { src: '/assets/fullviewportatacama.jpg', alt: 'Awasi Atacama Villa' },
    { src: '/assets/atacama-factsheet-hero.jpg', alt: 'Pool Reflection' },
    { src: '/assets/experiencepagebreak.jpg', alt: 'Desert Experience' },
    { src: '/assets/images/video1.png', alt: 'Flamingos at Chaxa' },
    { src: '/assets/images/video2.png', alt: 'Rainbow Valley' },
    { src: '/assets/images/video3.png', alt: 'Horseback Riding' },
    { src: '/assets/images/video4.png', alt: 'Star Gazing' }
  ];

  return (
    <div className="gallery-page gallery-atacama">
      {/* Navigation - Atacama variant */}
      <UnifiedNavigation variant="light" destination="atacama" />

      {/* Hero Section */}
      <div 
        className="gallery-hero-section"
        style={{ 
          backgroundImage: `linear-gradient(90deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.4) 100%), url('${images.galleryHeroBackground || '/assets/atacama-factsheet-hero.jpg'}')`,
        }}
      >
        <div className="gallery-hero-content">
          <h1 className="gallery-hero-title">
            <ContentfulText 
              content={pageContent?.heroTitle} 
              fallback="Awaken to Unforgettable Views"
            />
          </h1>
        </div>
      </div>

      {/* Gallery Content Text */}
      <div className="gallery-content-text">
        <h2>
          <ContentfulText 
            content={pageContent?.contentText} 
            fallback="Take a seat in the front row, watching the theatre of mother nature unfold."
          />
        </h2>
      </div>

      {/* Filters Section - Filtered for Atacama only */}
      <FilterBar
        className="gallery-filters"
        dropdowns={[
          {
            id: 'destination',
            type: 'destination',
            level: 'brand',
            options: [{ id: 'atacama', value: 'atacama', label: 'ATACAMA' }],
            selectedValue: selectedDestination,
            onSelect: (value: string) => setSelectedDestination(value)
          },
          {
            id: 'type',
            type: 'type',
            level: 'brand',
            options: TYPE_OPTIONS,
            selectedValue: selectedType,
            onSelect: (value: string) => setSelectedType(value === selectedType ? undefined : value)
          }
        ]}
        alignment="center"
      />

      {/* Main Gallery - Structured Layout Based on Figma */}
      <div className="gallery-container" data-scroll-section="gallery">
        <div className="gallery-masonry-grid">
          
          {/* Pattern 1: Full width landscape */}
          <div className="gallery-row">
            <div className="gallery-item full-width">
              <ContentfulImage imageKey="gallery-main" fallbackSrc={getImageUrl('/assets/images/gallery-atacama-desert-view.png', 0)} />
            </div>
          </div>

          {/* Pattern 2: Large + Small */}
          <div className="gallery-row">
            <div className="gallery-item large">
              <ContentfulImage imageKey="gallery-main" fallbackSrc={getImageUrl('/assets/images/destinationsAtacama.png', 1)} />
            </div>
            <div className="gallery-item small">
              <ContentfulImage imageKey="gallery-main" fallbackSrc={getImageUrl('/assets/images/packageCollage1.png', 2)} />
            </div>
          </div>

          {/* Pattern 3: Full width landscape */}
          <div className="gallery-row">
            <div className="gallery-item full-width">
              <ContentfulImage imageKey="gallery-main" fallbackSrc={getImageUrl('/assets/images/packageCollage2.png', 3)} />
            </div>
          </div>

          {/* Pattern 4: Three column grid */}
          <div className="gallery-row three-columns">
            <div className="gallery-item">
              <ContentfulImage imageKey="gallery-main" fallbackSrc={getImageUrl('/assets/images/packageCollage3.png', 4)} />
            </div>
            <div className="gallery-item">
              <ContentfulImage imageKey="gallery-main" fallbackSrc={getImageUrl('/assets/fullviewportatacama.jpg', 5)} />
            </div>
            <div className="gallery-item">
              <ContentfulImage imageKey="gallery-wildlife-1" fallbackSrc={getImageUrl('/assets/atacama-factsheet-hero.jpg', 6)} />
            </div>
          </div>

          {/* Repeat Pattern 1 */}
          <div className="gallery-row">
            <div className="gallery-item full-width">
              <ContentfulImage imageKey="gallery-main" fallbackSrc={getImageUrl('/assets/experiencepagebreak.jpg', 7)} />
            </div>
          </div>

          {/* Repeat Pattern 2 */}
          <div className="gallery-row">
            <div className="gallery-item large">
              <ContentfulImage imageKey="gallery-main" fallbackSrc={getImageUrl('/assets/images/video1.png', 8)} />
            </div>
            <div className="gallery-item small">
              <ContentfulImage imageKey="gallery-video-2" fallbackSrc={getImageUrl('/assets/images/video2.png', 9)} />
            </div>
          </div>

          {/* Repeat Pattern 3 */}
          <div className="gallery-row">
            <div className="gallery-item full-width">
              <ContentfulImage imageKey="gallery-main" fallbackSrc={getImageUrl('/assets/images/video3.png', 10)} />
            </div>
          </div>

          {/* Repeat Pattern 4 */}
          <div className="gallery-row three-columns">
            <div className="gallery-item">
              <ContentfulImage imageKey="gallery-video-4" fallbackSrc={getImageUrl('/assets/images/video4.png', 11)} />
            </div>
            <div className="gallery-item">
              <ContentfulImage imageKey="gallery-culture-2" fallbackSrc={getImageUrl('/assets/images/gallery-atacama-desert-view.png', 12)} />
            </div>
            <div className="gallery-item">
              <ContentfulImage imageKey="gallery-culture-3" fallbackSrc={getImageUrl('/assets/images/destinationsAtacama.png', 13)} />
            </div>
          </div>

          {/* Continue pattern... */}
          <div className="gallery-row">
            <div className="gallery-item full-width">
              <ContentfulImage imageKey="gallery-wildlife-1" fallbackSrc={getImageUrl('/assets/images/packageCollage1.png', 14)} />
            </div>
          </div>

          <div className="gallery-row">
            <div className="gallery-item large">
              <ContentfulImage imageKey="gallery-main" fallbackSrc={getImageUrl('/assets/images/packageCollage2.png', 15)} />
            </div>
            <div className="gallery-item small">
              <ContentfulImage imageKey="gallery-wildlife-1" fallbackSrc={getImageUrl('/assets/images/packageCollage3.png', 16)} />
            </div>
          </div>

          <div className="gallery-row">
            <div className="gallery-item full-width">
              <ContentfulImage imageKey="gallery-main" fallbackSrc={getImageUrl('/assets/fullviewportatacama.jpg', 17)} />
            </div>
          </div>

          <div className="gallery-row three-columns">
            <div className="gallery-item">
              <ContentfulImage imageKey="gallery-wildlife-2" fallbackSrc={getImageUrl('/assets/atacama-factsheet-hero.jpg', 18)} />
            </div>
            <div className="gallery-item">
              <ContentfulImage imageKey="gallery-landscape-1" fallbackSrc={getImageUrl('/assets/experiencepagebreak.jpg', 19)} />
            </div>
            <div className="gallery-item">
              <ContentfulImage imageKey="gallery-landscape-2" fallbackSrc={getImageUrl('/assets/images/video1.png', 20)} />
            </div>
          </div>

          {/* More variations */}
          <div className="gallery-row">
            <div className="gallery-item full-width">
              <ContentfulImage imageKey="gallery-main" fallbackSrc={getImageUrl('/assets/images/video2.png', 21)} />
            </div>
          </div>

          <div className="gallery-row">
            <div className="gallery-item large">
              <ContentfulImage imageKey="gallery-main" fallbackSrc={getImageUrl('/assets/images/video3.png', 22)} />
            </div>
            <div className="gallery-item small">
              <ContentfulImage imageKey="gallery-main" fallbackSrc={getImageUrl('/assets/images/video4.png', 23)} />
            </div>
          </div>

          <div className="gallery-row">
            <div className="gallery-item full-width">
              <ContentfulImage imageKey="gallery-main" fallbackSrc={getImageUrl('/assets/images/gallery-atacama-desert-view.png', 24)} />
            </div>
          </div>

          <div className="gallery-row three-columns">
            <div className="gallery-item">
              <ContentfulImage imageKey="gallery-main" fallbackSrc={getImageUrl('/assets/images/destinationsAtacama.png', 25)} />
            </div>
            <div className="gallery-item">
              <ContentfulImage imageKey="gallery-main" fallbackSrc={getImageUrl('/assets/images/packageCollage1.png', 26)} />
            </div>
            <div className="gallery-item">
              <ContentfulImage imageKey="gallery-main" fallbackSrc={getImageUrl('/assets/images/packageCollage2.png', 27)} />
            </div>
          </div>

          {/* Final pattern repeat */}
          <div className="gallery-row">
            <div className="gallery-item full-width">
              <ContentfulImage imageKey="gallery-main" fallbackSrc={getImageUrl('/assets/images/packageCollage3.png', 28)} />
            </div>
          </div>

          <div className="gallery-row">
            <div className="gallery-item large">
              <ContentfulImage imageKey="gallery-landscape-1" fallbackSrc={getImageUrl('/assets/fullviewportatacama.jpg', 29)} />
            </div>
            <div className="gallery-item small">
              <ContentfulImage imageKey="gallery-wildlife-2" fallbackSrc={getImageUrl('/assets/atacama-factsheet-hero.jpg', 30)} />
            </div>
          </div>

        </div>
      </div>

      {/* Instagram Section */}
      <InstagramSection 
        title={t('instagram.title')}
        posts={[
          { src: getImageUrl('/assets/images/video1.png', 100), alt: getAltText("Instagram 1", 100) },
          { src: getImageUrl('/assets/images/video2.png', 101), alt: getAltText("Instagram 2", 101) },
          { src: getImageUrl('/assets/images/video3.png', 102), alt: getAltText("Instagram 3", 102) },
          { src: getImageUrl('/assets/images/video4.png', 103), alt: getAltText("Instagram 4", 103) }
        ]}
      />

      {/* Footer with Atacama variant */}
      <Footer variant="atacama" />
    </div>
  );
};

export default GalleryAtacama;